// Single source of truth for "is this guest signed in", and the only place that
// tears a session down.
//
// Auth state used to live in a cookie and in localStorage at the same time, and
// each of the places that signed a guest out cleared a different mix of the two.
// The token is what the API actually authenticates against, so its own `exp`
// claim is the expiry we read here.

const TOKEN_KEY = "token";
const USER_ID_KEY = "user_id";

// Listed explicitly so signing out never reaches beyond our own keys the way
// localStorage.clear() did.
const SESSION_KEYS = [TOKEN_KEY, USER_ID_KEY, "user", "userName"];

// The token was mirrored into cookies the backend never read. We no longer
// write them, but existing browsers still hold them, so signing out has to
// expire them or a stale cookie outlives the session that created it.
// `redirectPath` is deliberately not in this list: it has to survive the trip
// through the login wall.
const LEGACY_COOKIES = ["Token", "UserId"];

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const getUserId = () => localStorage.getItem(USER_ID_KEY);

const decodeTokenPayload = (token) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return null;
    }
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch (error) {
    return null;
  }
};

// Anything we cannot read an expiry out of counts as expired: the API rejects
// tokens with no `exp` claim, so trusting one here only defers the failure to a
// 401 on the next request.
export const isTokenExpired = (token) => {
  const payload = decodeTokenPayload(token);
  if (!payload || typeof payload.exp !== "number") {
    return true;
  }
  return payload.exp * 1000 <= Date.now();
};

export const hasValidSession = () => {
  const token = getToken();
  if (!token || !getUserId()) {
    return false;
  }
  return !isTokenExpired(token);
};

const expireCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
};

export const clearSession = () => {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  LEGACY_COOKIES.forEach(expireCookie);
};

export const storeSession = (token, userId) => {
  localStorage.setItem(TOKEN_KEY, token);
  if (userId) {
    localStorage.setItem(USER_ID_KEY, userId);
  }
};
